import { useEffect, useRef, useState } from "react";
import { shallowEqual, useDispatch, useSelector } from "react-redux";
import { Form } from "react-bootstrap";

import * as actions from "@/redux/actions";
import RightSideBar from "@widgets/RightSideBar/RightSideBar";
import RightSidebarGroup from "@widgets/RightSideBar/RightSidebarGroup";
import SntOnOffSwitch from "@/cleanup/wrappers/Switch/SntOnOffSwitch";
import { AssetTag, SuccessTag } from "@cleanup/components/TagMore";
import { SensolusDatetimeReact } from "@/constants/Config";
import SntLocationStopIcon from "@icons/SntLocationStopIcon";
import SntReleaseNoteIcon from "@icons/SntReleaseNoteIcon";
import notificationClient from "@/apis/notificationClient";
import moment from "@/constants/SensolusMoment";
import SntDialog from "@/components/SntDialog/SntDialog";
import SntLink from "@/components/ReactBootstrap/SntLink";
import SntMarkdown from "@/cleanup/widgets/markdown/SntMarkdown";
import { RootState, NotificationMessage } from "@/types/RootState";
import { LanguageKey } from "@/types/generated/LanguageKey";
import { translate } from "@/hooks/useLanguage";

interface NotificationItemProps {
  item: NotificationMessage;
  language: string;
  contentClass: string;
  showDivider?: boolean;
}

const NotificationItem: React.FC<NotificationItemProps> = ({
  item,
  language,
  contentClass,
  showDivider = false,
}) => {
  const content =
    item.contentByLanguage[language] || item.contentByLanguage["en"];
  const title =
    item.titlesByLanguage[language] || item.titlesByLanguage["en"];

  const titleWithBadge = (
    <span className="d-flex align-items-center">
      {title}
      {!item.hasRead && (
        <SuccessTag tagName={"New"} className="ms-2" size={"sm"} />
      )}
    </span>
  );

  return (
    <RightSidebarGroup
      icon={<SntReleaseNoteIcon size={24} color="#424B59" />}
      title={titleWithBadge}
      showDivider={showDivider}
    >
      <div>
        {item.tags &&
          item.tags.map((tag, index) => (
            <AssetTag
              key={index}
              tagName={tag}
              className="me-2 mb-1"
              size={"sm"}
            />
          ))}
      </div>
      <div className="mb-1">
        <Form.Text>
          {SensolusDatetimeReact.parse(item.publishDate, "server").format(
            "DD MMMM yyyy"
          )}
        </Form.Text>
      </div>
      <SntMarkdown text={content} className={contentClass} />
    </RightSidebarGroup>
  );
};

const NotificationSidebar: React.FC = () => {
  const { showNotification, notifications = [] } = useSelector(
    (state: RootState) => state.all,
    shallowEqual
  );

  const loginInfo = useSelector((state: RootState) => state.user);

  const dispatch = useDispatch();

  const [checked, setChecked] = useState(false);
  const [directNotification, setDirectNotification] = useState<
    NotificationMessage[]
  >([]);

  useEffect(() => {
    getMessages();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const getMessages = () => {
    const fromDate = moment().subtract(90, "days").startOf("day");
    notificationClient
      .getFromDate(fromDate.format())
      .then(({ data }) => {
        const typedData = data as NotificationMessage[];
        const newMessages = typedData.filter((item) => !item.hasRead);
        dispatch(actions.setGlobalNotification(typedData));
        dispatch(actions.setGlobalNotificationCount(newMessages.length));
        // Only show popup notifications for normal users
        const isNormalUser = loginInfo.userType === "NORMAL";
        setDirectNotification(
          isNormalUser
            ? typedData.filter((item) => item.showAsPopup && !item.hasRead)
            : []
        );
      })
      .catch(() => {});
  };

  const onClose = (ids: (number | string)[]) => {
    notificationClient
      .makeAsRead(ids)
      .then(() => {
        const newData = notifications.map((item) => ({ ...item, hasRead: true }));
        dispatch(actions.setGlobalNotification(newData));
        dispatch(actions.setGlobalNotificationCount(0));
        setDirectNotification([]);
      })
      .catch(() => {});
  };

  // Mark everything read whenever the sidebar goes from shown to hidden,
  // however it was closed: its own close button, a click outside, or the
  // header bell toggling it off (STIC-16336).
  const wasShown = useRef(showNotification);
  useEffect(() => {
    if (wasShown.current && !showNotification) {
      onClose(notifications.map((item) => item.id));
    }
    wasShown.current = showNotification;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [showNotification]);

  return (
    <>
      <SntDialog
        isShow={directNotification.length > 0}
        isShowSaveButton={false}
        title={translate(LanguageKey.notifications_key)}
        onClose={() => onClose(directNotification.map((item) => item.id))}
      >
        {() => (
          <>
            {directNotification.map((item, key) => {
              return (
                <NotificationItem
                  key={key}
                  item={item}
                  language={loginInfo.language || "en"}
                  contentClass="notification-message-container-popup"
                />
              );
            })}
          </>
        )}
      </SntDialog>
      {showNotification && (
        <RightSideBar
          onClose={() => {
            dispatch(actions.setShowGlobalNotification(false));
          }}
          loading={false}
          title={translate(LanguageKey.notifications_key)}
        >
          {loginInfo.hideNotifications ? (
            <>{translate(LanguageKey.notification_sidebar_hide_info_key)}</>
          ) : (
            <>
              <div className="d-flex align-items-center mb-3">
                <div className="me-4">
                  {translate(LanguageKey.only_show_unread_key)}
                </div>
                <div>
                  <SntOnOffSwitch checked={checked} onChange={setChecked} />
                </div>
              </div>
              {notifications
                .filter((item) => {
                  if (checked === true) {
                    return !item.hasRead;
                  }
                  return true;
                })
                .map((item, index) => {
                  return (
                    <NotificationItem
                      key={item.id}
                      item={item}
                      language={loginInfo.language || "en"}
                      contentClass="notification-message-container"
                      showDivider={index > 0}
                    />
                  );
                })}
              <hr style={{ border: "none", borderTop: "1px solid #e7e7e2", margin: "16px 0", opacity: 1, height: 0 }} />
              <div className="text-center">
                <SntLocationStopIcon size={36} className="mb-3" />
                <div className="mb-3">
                  {translate(LanguageKey.notification_sidebar_info_key)}
                </div>
                <div>
                  <SntLink to="/profile">
                    {translate(LanguageKey.notification_sidebar_info2_key)}
                  </SntLink>
                </div>
              </div>
            </>
          )}
        </RightSideBar>
      )}
    </>
  );
};

export default NotificationSidebar;
